import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	Input,
	output,
} from '@angular/core';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { AssetTimeSeriesService } from '@app/pages/cmms/components/assets/asset-time-series/misc/asset-time-series-service';

import { first, map } from 'rxjs/operators';
import { AssetChartSettingsComponent } from '@app/pages/cmms/components/assets/asset-time-series/chart-with-settings/asset-chart-settings/asset-chart-settings.component';
import { ChartGraphComponent } from '@app/pages/cmms/components/assets/asset-time-series/chart-with-settings/chart-graph/chart-graph.component';
import { TimeSeriesWithoutPagination } from '@app/pages/cmms/components/assets/asset-time-series/misc/interfaces';
import { EmptyPlaceholderComponent } from '@app/shared/ui-components/empty-placeholder/empty-placeholder.component';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';

@Component({
	selector: 'chart-section',
	templateUrl: './chart-section.component.html',
	styleUrls: ['./chart-section.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ChartGraphComponent,
		AssetChartSettingsComponent,
		EmptyPlaceholderComponent,
		NgxTolgeeModule,
	],
})
export class ChartSectionComponent {
	private readonly snackBarService = inject(IconSnackBarService);
	private readonly assetTimeSeriesService: AssetTimeSeriesService =
		inject(AssetTimeSeriesService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly translateService = inject(TranslateService);

	@Input() set chart(data: TimeSeriesWithoutPagination) {
		this.chartData = data;
		this.cdr.detectChanges();
	}

	openAddValue = output();

	chartData: TimeSeriesWithoutPagination;

	savEditedDataPointChanges($event: { value: number; date: Date; pk: string }) {
		this.assetTimeSeriesService
			.updateAssetTimeSeriesData($event.pk, $event.value, $event.date)
			.pipe(first())
			.subscribe({
				next: updatedTimeSeries => {
					let timeSeries = this.chartData.timeSeries;

					timeSeries = timeSeries.map(ts =>
						ts.pk === updatedTimeSeries?.pk ? updatedTimeSeries : ts
					);

					this.chartData.timeSeries = timeSeries;

					this.assetTimeSeriesService.timeSeries$.next(
						this.assetTimeSeriesService.timeSeries$.value.map(chart =>
							chart.pk === this.chartData.pk ? this.chartData : chart
						)
					);

					this.cdr.detectChanges();

					this.snackBarService.success(
						this.translateService.instant('data_point_updated')
					);
				},
				error: error => {
					console.error(error);
					this.snackBarService.error(
						this.translateService.instant('failed_to_update_data_point')
					);
				},
			});
	}

	deleteDataPoint($event: { pk: string }) {
		this.assetTimeSeriesService.deleteAssetTimeSeriesData($event.pk).subscribe({
			next: () => {
				this.chartData.timeSeries = this.chartData.timeSeries.filter(
					ts => ts.pk !== $event.pk
				);

				this.assetTimeSeriesService.timeSeries$.next(
					this.assetTimeSeriesService.timeSeries$.value.map(chart =>
						chart.pk === this.chartData.pk ? this.chartData : chart
					)
				);
				this.cdr.detectChanges();

				this.snackBarService.success(this.translateService.instant('data_point_deleted'));
			},
			error: error => {
				console.error(error);
				this.snackBarService.error(
					this.translateService.instant('failed_to_delete_data_point')
				);
			},
		});
	}

	saveNewSettings(event: Pick<TimeSeriesWithoutPagination, 'settings' | 'description'>) {
		this.assetTimeSeriesService
			.editAssetTimeSeries(this.chartData.pk, null, event.description, {
				...event.settings,
			})
			.pipe(first())
			.subscribe({
				next: updatedSettings => {
					this.assetTimeSeriesService.timeSeries$
						.pipe(
							first(),
							map(timeSeries => {
								const updatedChart = timeSeries.find(
									chart => chart.pk === this.chartData.pk
								);
								updatedChart.settings = updatedSettings;
								updatedChart.description = event.description;
								return timeSeries;
							})
						)
						.subscribe(value => {
							this.assetTimeSeriesService.updateStoredTimeSeriesData(value);
							this.snackBarService.success(
								this.translateService.instant('settings_saved')
							);
						});
				},
				error: error => {
					console.error(error);
					this.snackBarService.error(
						this.translateService.instant('failed_to_save_settings')
					);
				},
			});
	}
}
